import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    addEdge,
    applyEdgeChanges,
    applyNodeChanges,
    BaseEdge,
    ReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from 'dagre';
import { parseDuration } from '../utils/time';
import { nodeTypes } from './StepNode';
import './RecipeGraph.css';

const COMPACT_NODE_HEIGHT = 50;
const LEVEL_GAP = 12;

// Custom L-shaped edge: straight lines with a single 90-degree bend
function LShapeEdge({ sourceX, sourceY, targetX, targetY, targetPosition, style }) {
    let path;
    if (targetPosition === 'left' || targetPosition === 'right') {
        // L-shape: go down from source to target's Y, then across to target
        path = `M ${sourceX} ${sourceY} L ${sourceX} ${targetY} L ${targetX} ${targetY}`;
    } else if (Math.abs(sourceX - targetX) < 2) {
        // Vertically aligned: straight line
        path = `M ${sourceX} ${sourceY} L ${targetX} ${targetY}`;
    } else {
        // Top handle with horizontal offset: step down, across, then down
        const midY = sourceY + (targetY - sourceY) * 0.5;
        path = `M ${sourceX} ${sourceY} L ${sourceX} ${midY} L ${targetX} ${midY} L ${targetX} ${targetY}`;
    }

    return <BaseEdge path={path} style={style} />;
}

const edgeTypes = { lshape: LShapeEdge };

function calculateLevelYPositions(nodes) {
    const levels = new Set();
    nodes.forEach(node => levels.add(node.data.level));

    const sortedLevels = [...levels].sort((a, b) => a - b);
    const levelYPositions = {};

    sortedLevels.forEach((level, index) => {
        levelYPositions[level] = index * (COMPACT_NODE_HEIGHT + LEVEL_GAP);
    });

    return levelYPositions;
}

const isMobile = () => window.innerWidth <= 600;
const COMPACT_PADDING = 8;

// Compute axis-aligned line segments for an edge based on current node positions
function computeEdgeSegments(edge, nodesMap, nodeWidth) {
    const source = nodesMap[edge.source];
    const target = nodesMap[edge.target];
    if (!source || !target) return [];

    const dx = target.position.x - source.position.x;
    const sourceX = source.position.x + nodeWidth / 2;
    const sourceY = source.position.y + COMPACT_NODE_HEIGHT;

    let targetX, targetY, targetHandle;
    if (Math.abs(dx) > nodeWidth * 0.5) {
        targetHandle = dx > 0 ? 'left' : 'right';
        targetX = targetHandle === 'left' ? target.position.x : target.position.x + nodeWidth;
        targetY = target.position.y + COMPACT_NODE_HEIGHT / 2;
    } else {
        targetHandle = 'top';
        targetX = target.position.x + nodeWidth / 2;
        targetY = target.position.y;
    }

    const segments = [];
    if (targetHandle === 'left' || targetHandle === 'right') {
        segments.push({ x1: sourceX, y1: sourceY, x2: sourceX, y2: targetY });
        segments.push({ x1: Math.min(sourceX, targetX), y1: targetY, x2: Math.max(sourceX, targetX), y2: targetY });
    } else if (Math.abs(sourceX - targetX) < 2) {
        segments.push({ x1: sourceX, y1: sourceY, x2: sourceX, y2: targetY });
    } else {
        const midY = sourceY + (targetY - sourceY) * 0.5;
        segments.push({ x1: sourceX, y1: sourceY, x2: sourceX, y2: midY });
        segments.push({ x1: Math.min(sourceX, targetX), y1: midY, x2: Math.max(sourceX, targetX), y2: midY });
        segments.push({ x1: targetX, y1: midY, x2: targetX, y2: targetY });
    }
    return segments;
}

// Find the leftmost valid x >= topX that doesn't collide with any edge segment or node
function findMinValidX(topX, origX, nodeY, nodeWidth, segments, otherNodes) {
    const p = COMPACT_PADDING;
    const forbidden = [];

    for (const seg of segments) {
        const isVertical = seg.x1 === seg.x2;
        if (isVertical) {
            const sy1 = Math.min(seg.y1, seg.y2);
            const sy2 = Math.max(seg.y1, seg.y2);
            if (sy2 < nodeY - p || sy1 > nodeY + COMPACT_NODE_HEIGHT + p) continue;
            forbidden.push([seg.x1 - nodeWidth - p, seg.x1 + p]);
        } else {
            if (seg.y1 < nodeY - p || seg.y1 > nodeY + COMPACT_NODE_HEIGHT + p) continue;
            const sx1 = Math.min(seg.x1, seg.x2);
            const sx2 = Math.max(seg.x1, seg.x2);
            forbidden.push([sx1 - nodeWidth - p, sx2 + p]);
        }
    }

    for (const other of otherNodes) {
        const oy = other.position.y;
        if (oy + COMPACT_NODE_HEIGHT <= nodeY || oy >= nodeY + COMPACT_NODE_HEIGHT) continue;
        forbidden.push([other.position.x - nodeWidth - p, other.position.x + nodeWidth + p]);
    }

    if (forbidden.length === 0) return topX;

    forbidden.sort((a, b) => a[0] - b[0]);
    const merged = [forbidden[0].slice()];
    for (let i = 1; i < forbidden.length; i++) {
        const last = merged[merged.length - 1];
        if (forbidden[i][0] <= last[1]) {
            last[1] = Math.max(last[1], forbidden[i][1]);
        } else {
            merged.push(forbidden[i].slice());
        }
    }

    let x = topX;
    for (const [start, end] of merged) {
        if (x >= start && x <= end) {
            x = end;
        }
    }
    return Math.min(x, origX);
}

// Compact each connected component by moving nodes left toward the topmost node's x
function compactLayout(nodes, edges, nodeWidth) {
    const componentGroups = {};
    nodes.forEach((node, i) => {
        if (node.data.isFinal) return;
        const cc = node.data.connectedComponent;
        if (cc === undefined || cc === null) return;
        if (!componentGroups[cc]) componentGroups[cc] = [];
        componentGroups[cc].push(i);
    });

    const nodesMap = {};
    nodes.forEach(n => { nodesMap[n.id] = n; });

    for (const nodeIndices of Object.values(componentGroups)) {
        nodeIndices.sort((a, b) => {
            const levelDiff = nodes[a].data.level - nodes[b].data.level;
            if (levelDiff !== 0) return levelDiff;
            return nodes[a].position.x - nodes[b].position.x;
        });

        const topLevel = nodes[nodeIndices[0]].data.level;
        let topX = Infinity;
        for (const idx of nodeIndices) {
            if (nodes[idx].data.level === topLevel) {
                topX = Math.min(topX, nodes[idx].position.x);
            } else break;
        }

        for (const nodeIdx of nodeIndices) {
            const node = nodes[nodeIdx];
            if (node.position.x <= topX) continue;

            const otherEdges = edges.filter(e =>
                e.source !== node.id && e.target !== node.id
            );
            const segments = otherEdges.flatMap(e =>
                computeEdgeSegments(e, nodesMap, nodeWidth)
            );
            const otherNodes = nodes.filter(n => n.id !== node.id);

            node.position.x = findMinValidX(
                topX, node.position.x, node.position.y, nodeWidth, segments, otherNodes
            );
        }
    }
}

// Slide components together using per-level collision vectors
function slideComponentsTogether(layoutedNodes, edges, nodeWidth, levelYPositions) {
    const componentGroups = {};
    layoutedNodes.forEach((node, i) => {
        if (node.data.isFinal) return;
        const cc = node.data.connectedComponent;
        if (cc === undefined || cc === null) return;
        (componentGroups[cc] = componentGroups[cc] || []).push(i);
    });
    const componentIds = Object.keys(componentGroups).map(Number);
    if (componentIds.length === 0) return;

    // Order components left to right by leftmost node x
    componentIds.sort((a, b) => {
        const minA = Math.min(...componentGroups[a].map(i => layoutedNodes[i].position.x));
        const minB = Math.min(...componentGroups[b].map(i => layoutedNodes[i].position.x));
        return minA - minB;
    });

    const allLevels = [...new Set(layoutedNodes.map(n => n.data.level))].sort((a, b) => a - b);

    // Compute per-level [min, max] extent for a set of node indices, including edge segments
    function computeExtents(nodeIndices) {
        const nodesMap = {};
        layoutedNodes.forEach(n => { nodesMap[n.id] = n; });

        const profile = {};
        const nodeIds = new Set(nodeIndices.map(i => layoutedNodes[i].id));

        for (const idx of nodeIndices) {
            const node = layoutedNodes[idx];
            const level = node.data.level;
            if (!profile[level]) profile[level] = { min: Infinity, max: -Infinity };
            profile[level].min = Math.min(profile[level].min, node.position.x);
            profile[level].max = Math.max(profile[level].max, node.position.x + nodeWidth);
        }

        // Include edges where source is in this component (including edges to sinks)
        const relevantEdges = edges.filter(e => nodeIds.has(e.source));
        for (const edge of relevantEdges) {
            const targetNode = nodesMap[edge.target];
            let segments;
            if (targetNode && targetNode.data.isFinal) {
                // For sink edges, only include the vertical segment straight down from source
                const sourceNode = nodesMap[edge.source];
                if (!sourceNode) continue;
                const sx = sourceNode.position.x + nodeWidth / 2;
                segments = [{ x1: sx, y1: sourceNode.position.y + COMPACT_NODE_HEIGHT, x2: sx, y2: targetNode.position.y }];
            } else {
                segments = computeEdgeSegments(edge, nodesMap, nodeWidth);
            }
            for (const seg of segments) {
                const yMin = Math.min(seg.y1, seg.y2);
                const yMax = Math.max(seg.y1, seg.y2);
                const xMin = Math.min(seg.x1, seg.x2);
                const xMax = Math.max(seg.x1, seg.x2);

                for (const level of allLevels) {
                    const levelTop = levelYPositions[level];
                    const levelBottom = levelTop + COMPACT_NODE_HEIGHT;
                    if (yMax >= levelTop && yMin <= levelBottom) {
                        if (!profile[level]) profile[level] = { min: Infinity, max: -Infinity };
                        profile[level].min = Math.min(profile[level].min, xMin);
                        profile[level].max = Math.max(profile[level].max, xMax);
                    }
                }
            }
        }

        return profile;
    }

    const GAP = COMPACT_PADDING;

    // Step 1: Move leftmost component to touch left wall (x=0)
    const firstGroup = componentGroups[componentIds[0]];
    const firstProfile = computeExtents(firstGroup);
    const firstMinX = Math.min(...Object.values(firstProfile).map(p => p.min));
    for (const idx of firstGroup) {
        layoutedNodes[idx].position.x -= firstMinX;
    }

    let placedIndices = [...firstGroup];

    // Step 2: For each subsequent component, slide it left against the placed ones
    for (let c = 1; c < componentIds.length; c++) {
        const group = componentGroups[componentIds[c]];

        // Rightmost collision vector for all placed components
        const leftProfile = computeExtents(placedIndices);
        // Leftmost collision vector for the component being placed
        const rightProfile = computeExtents(group);

        // max shift = min over shared levels of (rightMin - leftMax - GAP)
        let shift = Infinity;
        for (const level of allLevels) {
            const rightMin = rightProfile[level]?.min ?? Infinity;
            const leftMax = leftProfile[level]?.max ?? -Infinity;
            if (rightMin === Infinity || leftMax === -Infinity) continue;
            shift = Math.min(shift, rightMin - leftMax - GAP);
        }

        if (shift === Infinity) {
            // No shared levels — move to left wall
            const rightMinX = Math.min(...Object.values(rightProfile).map(p => p.min));
            shift = rightMinX;
        }

        if (shift > 0) {
            for (const idx of group) {
                layoutedNodes[idx].position.x -= shift;
            }
        }

        placedIndices.push(...group);
    }
}

export function getLayoutedElements(nodes, edges) {
    const mobile = isMobile();
    const nodeWidth = mobile ? 160 : 220;

    const dagreGraph = new dagre.graphlib.Graph();
    dagreGraph.setDefaultEdgeLabel(() => ({}));
    dagreGraph.setGraph({ rankdir: 'TB', nodesep: 15, ranksep: 15 });
    nodes.forEach(n => dagreGraph.setNode(n.id, { width: nodeWidth, height: COMPACT_NODE_HEIGHT }));
    edges.forEach(e => dagreGraph.setEdge(e.source, e.target));
    dagre.layout(dagreGraph);

    // Level-based Y positions
    const levelYPositions = calculateLevelYPositions(nodes);

    // Build final positioned nodes using dagre X and level Y
    const layoutedNodes = nodes.map(node => ({
        ...node,
        position: {
            x: dagreGraph.node(node.id).x,
            y: levelYPositions[node.data.level],
        },
    }));

    // Compact connected components horizontally
    compactLayout(layoutedNodes, edges, nodeWidth);

    // Slide components together using per-level collision vectors
    slideComponentsTogether(layoutedNodes, edges, nodeWidth, levelYPositions);

    // Move final/sink nodes as far left as possible
    const finalIndices = [];
    layoutedNodes.forEach((node, i) => {
        if (node.data.isFinal) finalIndices.push(i);
    });
    if (finalIndices.length > 0) {
        const sinkNodesMap = {};
        layoutedNodes.forEach(n => { sinkNodesMap[n.id] = n; });
        const minX = Math.min(...layoutedNodes.map(n => n.position.x));

        for (const idx of finalIndices) {
            const node = layoutedNodes[idx];
            const otherEdges = edges.filter(e =>
                e.source !== node.id && e.target !== node.id
            );
            const segments = otherEdges.flatMap(e =>
                computeEdgeSegments(e, sinkNodesMap, nodeWidth)
            );
            const otherNodes = layoutedNodes.filter(n => n.id !== node.id);

            node.position.x = findMinValidX(
                minX, node.position.x, node.position.y, nodeWidth, segments, otherNodes
            );
        }
    }

    // Build map for handle assignment
    const nodesMap = {};
    layoutedNodes.forEach(n => { nodesMap[n.id] = n; });

    // Assign handles based on relative positions
    const layoutedEdges = edges.map(edge => {
        const source = nodesMap[edge.source];
        const target = nodesMap[edge.target];
        if (!source || !target) return edge;

        const dx = target.position.x - source.position.x;
        let targetHandle = 'top';
        if (Math.abs(dx) > nodeWidth * 0.5) {
            targetHandle = dx > 0 ? 'left' : 'right';
        }

        return {
            ...edge,
            sourceHandle: 'bottom',
            targetHandle,
        };
    });

    return { nodes: layoutedNodes, edges: layoutedEdges };
}

const componentColors = ['blue', 'green', 'amber', 'rose'];
const SINK_COLOR = 'slate';

function stepColor(step) {
    if (step.is_final) return SINK_COLOR;
    return componentColors[step.connected_component % componentColors.length];
}

export function createNodes(recipe) {
    const nodes = [];
    for (const step of recipe.steps) {
        nodes.push({
            id: step.id,
            type: 'stepNode',
            data: {
                label: step.title || step.description,
                description: step.description,
                preparationTime: parseDuration(step.preparation_time),
                waitingTime: parseDuration(step.waiting_time),
                prepSeconds: step.preparation_time,
                waitSeconds: step.waiting_time,
                ingredients: step.ingredients,
                timeUntilEnd: step.time_until_end,
                level: step.level,
                isFinal: step.is_final,
                connectedComponent: step.connected_component,
                color: stepColor(step),
            },
            position: { x: 0, y: 0 },
        });
    }
    return nodes;
}

const edgeColors = {
    green: '#86efac',
    amber: '#fcd34d',
    blue: '#93c5fd',
    rose: '#fda4af',
    slate: '#cbd5e1',
};

export function createEdges(recipe, nodeColorMap) {
    const edges = [];
    for (const step of recipe.steps) {
        if (step.predecessors) {
            for (const predecessor of step.predecessors) {
                const color = nodeColorMap[predecessor] || 'green';
                edges.push({
                    id: `${step.id}-${predecessor}`,
                    source: predecessor,
                    target: step.id,
                    type: 'lshape',
                    style: { stroke: edgeColors[color], strokeWidth: 2 },
                });
            }
        }
    }
    return edges;
}

export function GraphView({ recipe, globalEndTime }) {
    const initialNodes = createNodes(recipe);
    const nodeColorMap = {};
    initialNodes.forEach(n => { nodeColorMap[n.id] = n.data.color; });
    const initialEdges = createEdges(recipe, nodeColorMap);

    const [nodes, setNodes] = useState(initialNodes);
    const [edges, setEdges] = useState(initialEdges);
    const [isLayouting, setIsLayouting] = useState(true);
    const [expandedNodeId, setExpandedNodeId] = useState(null);

    useEffect(() => {
        setIsLayouting(true);
        try {
            const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
                initialNodes,
                initialEdges
            );
            setNodes(layoutedNodes);
            setEdges(layoutedEdges);
        } catch (error) {
            console.error('Layout failed:', error);
            setNodes(initialNodes);
            setEdges(initialEdges);
        } finally {
            setIsLayouting(false);
        }
    }, [recipe]); // eslint-disable-line react-hooks/exhaustive-deps

    const onNodesChange = useCallback(
        (changes) => setNodes((nds) => applyNodeChanges(changes, nds)),
        [],
    );
    const onEdgesChange = useCallback(
        (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
        [],
    );
    const onConnect = useCallback(
        (params) => setEdges((eds) => addEdge(params, eds)),
        [],
    );

    const stableEdgeTypes = useMemo(() => edgeTypes, []);

    if (isLayouting) {
        return (
            <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <div>Calculating layout...</div>
            </div>
        );
    }

    return (
        <div style={{ width: '100%', height: '100%' }}>
            <ReactFlow
                nodes={nodes.map(node => ({
                    ...node,
                    zIndex: node.id === expandedNodeId ? 1000 : 0,
                    data: {
                        ...node.data,
                        globalEndTime,
                        isExpanded: node.id === expandedNodeId,
                        onToggleExpand: setExpandedNodeId,
                    },
                }))}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                nodeTypes={nodeTypes}
                edgeTypes={stableEdgeTypes}
                onConnect={onConnect}
                fitView
                fitViewOptions={{ padding: 0.2 }}
                zoomOnScroll={false}
                panOnScroll={true}
            />
        </div>
    );
}
